<script setup lang="ts">
import { DatePicker } from 'v-calendar';
// import 'v-calendar/style.css';

// icons
import { faCalendarDay, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { faImage, faCloudUpload } from '@fortawesome/pro-solid-svg-icons';

// validation
import { defineRule, useForm, useField } from 'vee-validate';
import { required } from '@vee-validate/rules';

// components
import CiImageUploadElement from '@/components/upload/CiImageUploadElement.vue';
import CiModalSuccess from '@/components/CiModal/CiModalSuccess.vue';

defineRule('isChecked', (value) => {
  if (Number(value) === 0) {
    return false;
  }

  return required(value);
});

defineRule('rangeOfStay', (value) => {
  if (!value || value.start === null || value.end === null) {
    return false;
  }
  return true;
});

// defineRule('imageDescription', value => {
//   return value && value.description && value.description.length <= 1000 && value.description.length > 0;
// });

defineRule('imageSize', (value, [size]) => {
  if (!value) {
    return false;
  }
  return value.size < size;
});

defineRule('imageDimensions', (value, [width, height]) => {
  if (!value) {
    return false;
  }
  return !(value.width < width || value.height < height);
});

/********************
 * PROPS & EMITS     *
 ********************/
export interface CiFormsUploadPicturesProps {
  campsite: any;
}
const props = withDefaults(defineProps<CiFormsUploadPicturesProps>(), {});

/********************
 * COMPOSITIONS      *
 ********************/
const appStore = useAppStore();
const localePath = useLocalePath();
const pictureService = usePictureService();
const route = useRoute();
const { $gettext, $ngettext } = useGettext();
const { filesizeHumanReadable } = useFileSize();
const { getMsgWarning } = useSystemMessages();
const { i18AcceptTermsCheckboxText, i18nMarketingSignupLabel } = useI18nMeta();
const { locale } = useI18n();
const { openModal, closeModal } = useCiModal();

/********************
 * REFS & VARS       *
 ********************/
const formRef = ref<HTMLFormElement | null>(null);
const genericErrors = ref([]);
const loading = ref(false);
const monthOfStay = ref(null);
const pictureListHeaderRef = ref<HTMLFormElement | null>(null);
const pictures = reactive([]);
const submitted = ref(false);
const success = ref(false);

// form
const imageUploadRules = computed(() => {
  return `imageSize:${appStore.settings?.upload?.max_filesize_images}|imageDimensions:${appStore.settings?.upload?.min_imagesizes?.default[0]},${appStore.settings?.upload?.min_imagesizes?.default[1]}`;
});

const minDimension = computed(() => {
  return `${appStore.settings?.upload?.min_imagesizes?.default[0]}x${appStore.settings?.upload?.min_imagesizes?.default[1]} px`;
});

const schema = computed(() => {
  return pictures.reduce(
    (acc, _pic, idx) => {
      acc[`picture_${idx}`] = imageUploadRules.value;
      return acc;
    },
    {
      rangeOfStay: 'rangeOfStay',
      terms: 'isChecked',
    },
  );
});

const { validate } = useForm({
  validationSchema: schema,
});

const { value: termsValue, errorMessage: termsError } = useField<boolean>('terms');
const { value: rangeOfStayValue, errorMessage: rangeOfStayError } = useField<{ start: Date; end: Date }>('rangeOfStay');
const { value: marketingSignupValue } = useField<boolean>('marketingSignup');

/********************
 * FUNCTIONS         *
 ********************/
watch(() => pictures, onPicturesChange);

function onMarketingSignupClicked(payload: boolean) {
  marketingSignupValue.value = payload;
}

function onPicturesChange(newVal, oldVal) {
  nextTick(() => {
    if (newVal.length > oldVal.length) {
      pictureListHeaderRef.value?.scrollIntoView({ behavior: 'smooth' });
    }
  });
}

function onDateSelect(value) {
  if (!value || !value.start) {
    rangeOfStayValue.value = { start: null, end: null };
    monthOfStay.value = null;
    return;
  }

  monthOfStay.value = value.start?.toISOString().split('T')[0];
}

function onDropzoneFilesSelected(files) {
  [...files].forEach((pic) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const errors = [];
      const minSize = appStore.settings?.upload?.min_imagesizes?.default;
      const img = new Image();
      img.onload = () => {
        if (pic.size > appStore.settings.upload.max_filesize_images) {
          errors.push({
            message: $gettext('Image filesize is too large.'),
            code: 'image_too_large',
            hideCode: true,
          });
        }

        if (img.width < minSize[0] || img.height < minSize[1]) {
          errors.push({
            message: $gettext('Image is too small.'),
            code: 'wrong_image_size',
            hideCode: true,
          });
        }

        pictures.push({
          errors,
          file: pic,
          height: img.height,
          name: pic.name,
          size: pic.size,
          src: event.target.result,
          width: img.width,
        });
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(pic);
  });
}

function onTermsCheckboxClicked(payload) {
  termsValue.value = payload;
}

function successText(count) {
  const campsiteName = props.campsite?.meta?.name ? props.campsite.meta.name : 'unknown campsite';
  return $ngettext(
    'Your Image for %{campsite} was successfully uploaded.',
    'Your %{count} Images for %{campsite} where successfully uploaded.',
    count,
    { count, campsite: campsiteName.trim() },
    true,
  );
}

function openModalSuccess(metaData, count) {
  const warningKeys = Object.keys(metaData.warnings ? metaData.warnings : {});
  const warnings = [];
  warningKeys.forEach((key) => {
    warnings.push(getMsgWarning(key));
  });

  openModal(
    {
      component: CiModalSuccess,
      attrs: {
        name: 'CiModalSuccess',
        buttons: [
          {
            label: $gettext('Back to campsite'),
            to: localePath({
              name: RBN_CAMPSITE_DETAIL,
              params: { identifier: props.campsite.slug },
            }),
            classes: ['button--primary'],
          },
        ],
        closeable: false,
        successMessage: successText(count),
        title: $gettext('Saving process successful'),
        warnings,
        onClose: () => {
          closeModal('CiModalSuccess');
        },
      },
    },
    {
      clickToClose: false,
      contentTransition: 'translate-y-down',
      contentClass: 'max-w-[800px] w-full h-auto',
    },
  );
}

function cancel() {
  return navigateTo(localePath({ name: RBN_CAMPSITE_DETAIL, params: { identifier: route.params.identifier } }));
}

async function onSubmitClicked() {
  const { valid } = await validate();

  if (!valid) {
    scrollToError();
    return false;
  }

  submit();
}

function submit() {
  loading.value = true;
  success.value = false;
  genericErrors.value = [];

  scrollToElement(formRef.value, 'loading-anchor');

  const data = new FormData(document.forms.picturesUploadForm);
  data.delete('images');
  data.delete('descriptions');

  for (let i = 0; i < pictures.length; i += 1) {
    data.delete(`description_picture_${i}`);
    data.append('images', pictures[i].file);
    data.append('descriptions', pictures[i].description || '');
  }

  data.delete('marketingSignup');
  if (marketingSignupValue.value) {
    data.set('marketing_signup', 'true');
  }

  pictureService
    .create(locale.value, data)
    .then((res) => {
      if (res) {
        loading.value = false;
        success.value = true;
        openModalSuccess(res.meta, pictures.length);
        startOver();
        window.scrollTo(0, 0);
      }
    })
    .catch((err) => {
      if (err.response) {
        loading.value = false;
        if (err.response?._data) {
          Object.keys(err.response._data).forEach((key) => {
            const value = err.response._data[key];
            if (key === 'images' || key === 'descriptions') {
              for (const [idx, prop] of Object.entries(value)) {
                addImageError(idx, { code: key, message: prop[0].message });
              }
            } else {
              for (const [_, prop] of Object.entries(value)) {
                addGenericError(prop[0].message);
              }
            }
          });
          console.warn(err.response._data);
        }
      }
    });
}

function addImageError(idx, error) {
  if (pictures[idx].errors.filter(err => err.code === error.code).length) {
    return;
  }
  pictures[idx].errors.push(error);
}

function addGenericError(message) {
  genericErrors.value = [...genericErrors.value, message];
}

function scrollToError() {
  nextTick(() => {
    scrollToElement(formRef.value, 'has-error');
  });
}

function startOver() {
  formRef.value?.reset();
  genericErrors.value = [];
  loading.value = false;
  pictures.splice(0, pictures.length);
  submitted.value = false;
  success.value = false;
  nextTick(() => {
    document.forms.picturesUploadForm.reset();
  });
}

function onImageRemove(idx) {
  pictures.splice(idx, 1);
}

function onImageChange(data, idx) {
  pictures[idx] = data;
}
</script>

<template>
  <transition
    name="slide-fade"
    appear
  >
    <div class="mb-20">
      <div class="container">
        <form
          ref="formRef"
          name="picturesUploadForm"
          novalidate
          @submit.prevent="onSubmitClicked"
        >
          <input
            v-if="props.campsite"
            :value="props.campsite.slug"
            type="hidden"
            name="campsite"
          />
          <input
            :value="locale"
            type="hidden"
            name="lang"
          />
          <input
            :value="monthOfStay"
            type="hidden"
            name="month_of_stay"
          />

          <div class="row loading-anchor">
            <div class="col-12">
              <h3>{{ $gettext('Upload Images') }}</h3>
              <p>{{ $gettext('Upload-Images-Subline') }}</p>
            </div>
            <div class="col-12">
              <CiDropzone
                :dropzone-pending="!campsite || loading"
                :has-error="pictures.length <= 0 && submitted"
                name="images"
                accept="image/jpeg,image/gif,image/png"
                type="multiple"
                @ci-dropzone-files-selected="onDropzoneFilesSelected"
              >
                <!-- meta-info -->
                <div class="mt-4 pt-1 text-sm text-gray md:flex">
                  <div class="my-4 flex md:my-0">
                    <CiAwesomeIcon
                      :icon="faImage"
                      class="hidden fill-gray md:block"
                      ratio="1.5"
                    />
                    <div class="ml-2">
                      <div class="font-bold">
                        {{ $gettext('Images') }}
                      </div>
                      <div v-if="appStore.settings">
                        <span>{{ $gettext('Accepted image formats are JPG, PNG, GIF') }}</span>
                        <div>
                          {{
                            $gettext('Maximum size: %{filesize}', {
                              filesize: filesizeHumanReadable(appStore.settings.upload.max_filesize_images, locale),
                            })
                          }}
                        </div>
                        <div>
                          {{ $gettext('Minimum dimension: %{dimension}', { dimension: minDimension }) }}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="my-4 flex md:my-0 md:ml-8">
                    <CiAwesomeIcon
                      :icon="faCloudUpload"
                      class="hidden fill-gray md:block"
                      ratio="1.5"
                    />
                    <div class="ml-2">
                      <div class="font-bold">
                        {{ $gettext('Total') }}
                      </div>
                      <div v-if="appStore.settings">
                        {{
                          $gettext('maximum size: %{filesize}', {
                            filesize: filesizeHumanReadable(appStore.settings.upload.max_postsize, locale),
                          })
                        }}
                      </div>
                    </div>
                  </div>
                </div>
              </CiDropzone>
            </div>

            <div
              v-if="pictures.length"
              ref="pictureListHeaderRef"
              class="col-12 mt-16"
            >
              <h3>{{ $gettext('Your images') }}</h3>
              <p>{{ $gettext('Please add descriptions to all your images.') }}</p>
            </div>

            <div class="col-12 picture-list">
              <CiImageUploadElement
                v-for="(picture, index) in pictures"
                :id="`picture_${index}`"
                :key="`picture.src-${index}`"
                :value="picture"
                :name="`picture_${index}`"
                :disabled="loading"
                :rules="imageUploadRules"
                @input="onImageChange($event, index)"
                @remove="onImageRemove(index)"
              />
            </div>
          </div>

          <!-- datePicker -->
          <div class="row mt-12">
            <div class="col-12 col-lg-5 col-md-6">
              <div
                class="mb-6"
                :class="{ 'has-error': rangeOfStayError }"
              >
                <label class="input-label font-medium"><span>{{ $gettext('Date of stay') }}</span><sup>*</sup></label>
                <div class="input-field input--inverted !p-0">
                  <DatePicker
                    v-model.range="rangeOfStayValue"
                    :locale="locale"
                    color="green"
                    :initial-page="{ month: new Date().getMonth() + 1, year: new Date().getFullYear() }"
                    :max-date="new Date()"
                    :masks="{ input: 'D. MMMM YYYY' }"
                    :update-on-input="false"
                    :columns="1"
                    name="rangeOfStay"
                    @update:model-value="onDateSelect"
                  >
                    <template #default="{ inputValue, togglePopover }">
                      <div
                        class="flex size-full flex-row items-center rounded p-4"
                        :class="[
                          loading ? 'pointer-events-none cursor-not-allowed bg-gray-30' : 'cursor-pointer bg-gray-10',
                        ]"
                        @click="togglePopover"
                      >
                        <CiAwesomeIcon
                          :icon="faCalendarDay"
                          class=""
                        />
                        <span
                          v-if="inputValue.start && inputValue.end"
                          class="ml-2"
                        >{{ inputValue.start }} - {{ inputValue.end }}</span>
                        <span
                          v-else
                          class="ml-2 text-gray-light"
                        > {{ $gettext('Select date of stay...') }}</span>
                        <CiAwesomeIcon
                          v-if="rangeOfStayValue?.start || rangeOfStayValue?.end"
                          :icon="faTimes"
                          class="ml-auto"
                          @click.stop.prevent="() => onDateSelect(null)"
                        />
                      </div>
                    </template>
                  </DatePicker>
                </div>
              </div>
            </div>
          </div>

          <div class="row mt-6">
            <div class="col-12">
              <InputCheckbox
                id="marketingSignup"
                :allow-html="true"
                :label="i18nMarketingSignupLabel"
                :value="marketingSignupValue"
                name="marketingSignup"
                :disabled="loading"
                @input="onMarketingSignupClicked"
              />
            </div>
          </div>

          <!-- terms -->
          <div class="row">
            <div class="col-12">
              <InputCheckbox
                id="terms"
                :allow-html="true"
                :label="i18AcceptTermsCheckboxText"
                :value="termsValue"
                :has-error="!!termsError"
                name="terms"
                @input="onTermsCheckboxClicked"
              />
            </div>
          </div>

          <!-- info panel & cta -->
          <div class="row mt-12">
            <div class="col-12">
              <div
                v-if="genericErrors.length > 0"
                class="has-error"
              >
                <CiInfoPanel
                  v-for="(errorText, index) in genericErrors"
                  :key="`genericError-${index}`"
                  :condition="true"
                  :text="errorText"
                  type="danger"
                />
              </div>
              <div
                v-if="!loading"
                class="row my-4"
              >
                <div class="col-12 col-md-6 col-lg-3 offset-lg-3 order-2 mt-4 md:order-1 md:mt-0">
                  <button
                    class="button button--dark-outline w-full"
                    type="button"
                    @click="cancel"
                  >
                    {{ $gettext('Cancel') }}
                  </button>
                </div>
                <div class="col-12 col-md-6 col-lg-3 order-1 md:order-2">
                  <button
                    class="button button--primary w-full"
                    type="submit"
                  >
                    <span>{{ $gettext('Add') }}</span>
                  </button>
                </div>
              </div>
              <CiInfoPanel
                v-else
                type="success"
                :condition="true"
                class="flex items-center p-4"
              >
                <CiSpinner
                  :flex="false"
                  size="md"
                />
                <p class="m-0 ml-3 p-0">
                  {{ $gettext('Uploading, please wait...') }}
                </p>
              </CiInfoPanel>
            </div>
          </div>
        </form>
      </div>
    </div>
  </transition>
</template>

<style></style>
